import { EventEmitter, Injectable } from '@angular/core';
import cornerstoneTools from 'cornerstone-tools/dist/cornerstoneTools.js';
const segModule = cornerstoneTools.getModule('segmentation');
const getLogger = cornerstoneTools.importInternal('util/getLogger');
const logger = getLogger('util:segmentation:labelmap3DHistory');
const { getters, setters } = segModule;
const external = cornerstoneTools.external;

import { IUndoRedoData, IUndoRedoEvent } from '@viewer/cornerstone.data';

@Injectable()
export class UndoRedoService {
  undoRedoEmitter: EventEmitter<IUndoRedoEvent> =
    new EventEmitter<IUndoRedoEvent>();

  constructor() {
    setters.undo = this.undo;
    setters.redo = this.redo;
  }

  /**
   * Method to undo last changes, needed here to
   * override the standard cornerstone's function
   * @method undo
   * @param {HTMLElement} element html element of viewer
   * @param {string} elementId id of html element of viewer
   * @param {number[]} hidenColors list of hiden colors for filter
   * @param {EventEmitter<IUndoRedoEvent>} undoRedoEmitter emitter for call function with update changed mask
   */
  public undo(
    element: HTMLElement,
    elementId: string,
    hidenColors: number[],
    undoRedoEmitter: EventEmitter<IUndoRedoEvent>
  ) {
    const labelmap3D = getters.labelmap3D(
      element,
      Number(elementId[elementId.length - 1])
    );
    const { undo, redo } = labelmap3D;

    if (!undo.length) {
      logger.warn('No undos left.');

      return;
    }

    // Pop last set of operations from undo.
    const operations = undo.pop();
    // Undo operations.
    const { labelmaps2D } = labelmap3D;
    const pixelDataForChangedMask: IUndoRedoData[] = [];
    operations.forEach((operation) => {
      const { imageIdIndex, diff } = operation;
      const labelmap2D = labelmaps2D[imageIdIndex];
      const pixelData = labelmap2D.pixelData;

      for (let i = 0; i < diff.length; i++) {
        const diffI = diff[i];

        pixelData[diffI[0]] = hidenColors![elementId]?.includes(diffI[1])
          ? 0
          : diffI[1];

        pixelDataForChangedMask.push({
          sliceIndex: imageIdIndex,
          pixelIndex: diffI[0],
          color: diffI[1],
        });
      }
    });
    undoRedoEmitter.emit({
      viewerId: elementId,
      actions: pixelDataForChangedMask,
    });
    // Push set of operations to redo.
    redo.push(operations);

    external.cornerstone.updateImage(element);
  }

  /**
   * Method to redo last changes, needed here to
   * override the standard cornerstone's function
   * @method redo
   * @param {HTMLElement} element html element of viewer
   * @param {string} elementId id of html element of viewer
   * @param {number[]} hidenColors list of hiden colors for filter
   * @param {EventEmitter<IUndoRedoEvent>} undoRedoEmitter emitter for call function with update changed mask
   */
  public redo(
    element: HTMLElement,
    elementId: string,
    hidenColors: number[],
    undoRedoEmitter: EventEmitter<IUndoRedoEvent>
  ) {
    const labelmap3D = getters.labelmap3D(
      element,
      Number(elementId[elementId.length - 1])
    );
    const { undo, redo } = labelmap3D;

    if (!redo.length) {
      logger.warn('No redos left.');

      return;
    }

    // Pop last set of operations from redo.
    const operations = redo.pop();
    // Redo operations.
    const { labelmaps2D } = labelmap3D;
    const pixelDataForChangedMask: IUndoRedoData[] = [];
    operations.forEach((operation) => {
      const { imageIdIndex, diff } = operation;
      const labelmap2D = labelmaps2D[imageIdIndex];
      const pixelData = labelmap2D.pixelData;

      for (let i = 0; i < diff.length; i++) {
        const diffI = diff[i];
        pixelData[diffI[0]] = hidenColors![elementId]?.includes(diffI[2])
          ? 0
          : diffI[2];
        pixelDataForChangedMask.push({
          sliceIndex: imageIdIndex,
          pixelIndex: diffI[0],
          color: diffI[2],
        });
      }
    });
    undoRedoEmitter.emit({
      viewerId: elementId,
      actions: pixelDataForChangedMask,
    });
    // Push set of operations to undo.
    undo.push(operations);

    external.cornerstone.updateImage(element);
  }
}
